import { normalizeUser, normalizeUsers } from "../utils/users";

const normalizeBaseUrl = (value) => String(value || "").trim().replace(/\/$/, "");

const buildApiBaseCandidates = () => {
  const candidates = [];
  const configuredBase = normalizeBaseUrl(process.env.REACT_APP_API_URL);

  if (configuredBase) {
    candidates.push(configuredBase);
  }

  if (typeof window !== "undefined") {
    const { hostname, port, origin, pathname } = window.location;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
    const isDevPort = port && port !== "80" && port !== "443";

    // React dev server cannot serve the PHP API directly, so try the Apache/XAMPP
    // backend path when we are on a localhost development origin.
    if (isLocalhost && isDevPort) {
      candidates.push("http://localhost/dp_portal_app/portal_bd/api");
      candidates.push("http://127.0.0.1/dp_portal_app/portal_bd/api");
    }

    // When the frontend is hosted from the same project folder in Apache, the API
    // sits alongside the portal_fd directory.
    if (pathname.includes("/dp_portal_app/portal_fd") || pathname.includes("/dp_portal_app/portal_bd")) {
      candidates.push(`${origin}/dp_portal_app/portal_bd/api`);
    }
  }

  candidates.push("/dp_portal_app/portal_bd/api");
  candidates.push("/portal_bd/api");

  return [...new Set(candidates.filter(Boolean))];
};

const API_BASE_CANDIDATES = buildApiBaseCandidates();

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const requestJson = async (path, options = {}) => {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  let lastError = null;

  for (const base of API_BASE_CANDIDATES) {
    try {
      const response = await fetch(`${base}${path}`, {
        credentials: "include",
        cache: "no-store",
        headers: isFormData
          ? {
              ...(options.headers || {}),
            }
          : {
              "Content-Type": "application/json",
              ...(options.headers || {}),
            },
        ...options,
      });

      const raw = await response.text();
      let payload = {};
      try {
        payload = raw ? JSON.parse(raw) : {};
      } catch {
        payload = { message: raw || `HTTP ${response.status}` };
      }

      if (response.ok) {
        return payload;
      }

      lastError = new Error(payload.message || `Request failed (${response.status}).`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to reach the API.");
};

export async function adminLogin(email, password) {
  return requestJson("/admin/login.php", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function saveAdminProfile(profile) {
  try {
    return await requestJson("/admin/profile.php", {
      method: "POST",
      body: JSON.stringify(profile),
    });
  } catch {
    writeJson("dp_admin_profile", profile);
    return { success: true, message: "Profile updated successfully." };
  }
}

export async function changeAdminPassword(payload) {
  return requestJson("/admin/change-password.php", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function logoutAdmin() {
  try {
    return await requestJson("/admin/logout.php", { method: "POST" });
  } catch {
    return { success: true };
  }
}

export async function listUsers() {
  const payload = await requestJson("/users/index.php", { method: "GET" });
  return {
    ...payload,
    users: normalizeUsers(payload.users),
  };
}

export async function saveUser(user) {
  return requestJson("/users/save.php", {
    method: "POST",
    body: JSON.stringify(user),
  });
}

export async function deleteUsers(ids) {
  return requestJson("/users/delete.php", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export async function clientLogin(passcode) {
  const payload = await requestJson("/client/login.php", {
    method: "POST",
    body: JSON.stringify({ passcode }),
  });
  return {
    ...payload,
    client: normalizeUser(payload.client),
  };
}
